import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ArticleFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ArticleConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { SettingsFormViewProps } from "models/propTypes/articlePropTypes";
import { FormikProps } from "formik";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { useInvoiceService } from "serviceHooks";
import { useSearchService } from "serviceHooks";
import NotVatRegisteredVatDescription from "./_fields/notVatRegisteredVatDescription";
//#endregion

const SettingsForm: ForwardRefRenderFunction<FormHandles, SettingsFormViewProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const searchService = useSearchService();
  const { units, canToggleActiveStatus, revenueAccounts, isAgentSaleArticle, noVatCompany } = props;

  const getRevenueAccountItems = (
    value: string,
    formattedValue?: string,
    formikProps?: FormikProps<ArticleFormTypes.Article>
  ) =>
    revenueAccounts?.accounts
      ?.filter((account) => account.vat === formikProps?.values.vat)
      .map((account) => ({
        id: account.number,
        value: account.number.toString(),
        description: account.name,
      })) ?? [];

  return (
    <GenericForm<ArticleFormTypes.Article>
      formRef={ref}
      {...props}
      fields={[
        {
          label: TranslationKeys.Common.General,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WInfo,
          navKey: ArticleConstants.SettingsNavKeys.General,
          innerFields: {
            name: {
              label: TranslationKeys.Common.Name,
              type: FormEnums.FormFieldType.TEXT,
              descriptionText: TranslationKeys.Article.NameDescription,
            },
            unitPrice: {
              label: TranslationKeys.Common.StandardPrice,
              type: FormEnums.FormFieldType.CURRENCY,
              descriptionText: TranslationKeys.Article.UnitPriceDescription,
              extraProps: {
                currency: CurrencyEnums.CurrencyCode.SEK,
              },
            },
            vatText: {
              label: TranslationKeys.Common.VatRate,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              descriptionText: noVatCompany ? (
                <NotVatRegisteredVatDescription />
              ) : (
                TranslationKeys.Article.VatDescription
              ),
              // Not VAT registered: locked like the "Aktiv" toggle below, with the reason in the description.
              disabled: noVatCompany,
              extraProps: {
                referenceFieldName: FormFieldConstants.Article.Article.Vat,
                items: searchService.vatTypes,
              },
              onChangeHandler: (_name: string, _value: any, formikProps: FormikProps<ArticleFormTypes.Article>) => {
                formikProps.setFieldValue(FormFieldConstants.Article.Article.Account, undefined);
                formikProps.setFieldValue(FormFieldConstants.Article.Article.AccountName, undefined);
              },
            },
            unit: {
              label: TranslationKeys.Invoice.Unit,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              descriptionText: TranslationKeys.Article.UnitDescription,
              extraProps: {
                suggestedItems: units,
                referenceFieldName: FormFieldConstants.Article.Article.UnitId,
                includeNone: true,
              },
            },
          },
        },
        {
          label: TranslationKeys.Common.Accounting,
          icon: IconDefinitionConstants.WList,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          navKey: ArticleConstants.SettingsNavKeys.Accounting,
          innerFields: {
            euVatCategory: {
              label: TranslationKeys.Invoice.EUVatCategory,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              descriptionText: TranslationKeys.Article.EuVatTypeDescription,
              extraProps: {
                referenceFieldName: FormFieldConstants.Article.Article.EuVatCategoryId,
                items: invoiceService.getEuVatCategoryTypes(),
              },
            },
            accountName: {
              label: TranslationKeys.Article.RevenueAccount,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              descriptionText: TranslationKeys.Article.RevenueAccountDescription,
              // Hidden both when the company has not opted in and for the agent flow's system
              // articles (R2-1). Without the second condition such an article renders the field
              // blank - its account (3015-3024 / 8415) sits outside the company's configured
              // intervals, so getRevenueAccountItems cannot resolve a name for it - and a plain
              // VAT edit then clears the account and is rejected as AgentArticleAccountLocked,
              // leaving the article uneditable from the UI entirely.
              hidden: revenueAccounts?.enabled !== true || isAgentSaleArticle === true,
              extraProps: {
                referenceFieldName: FormFieldConstants.Article.Article.Account,
                items: getRevenueAccountItems,
              },
            },
          },
        },
        {
          label: TranslationKeys.Invoice.DisplaySettings,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WEye,
          navKey: ArticleConstants.SettingsNavKeys.Display,
          innerFields: {
            active: {
              label: TranslationKeys.Common.Active,
              descriptionText: props.canToggleActiveStatus
                ? TranslationKeys.Article.ActivateArticleDescriptionText
                : TranslationKeys.Article.ActivateDiableDescriptionText,
              type: FormEnums.FormFieldType.TOGGLE,
              disabled: !canToggleActiveStatus,
            },
          },
        },
      ]}
      inline
    />
  );
};

export default forwardRef(SettingsForm);
